import React, { useState, useEffect, useRef } from 'react';
import { api } from './api.js';
import { jsPDF } from 'jspdf';
import { FiEye } from 'react-icons/fi';
import styles from './afiliates.module.css';
import AddAffiliateForm from './AddAffiliateForm';
import logo from './logo2.png';
import { hasPermission, PERMISSIONS, getUserData } from './permissions';

const Affiliates = () => {
  const [afiliados, setAfiliados] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [sectorSeleccionado, setSectorSeleccionado] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedAfiliado, setSelectedAfiliado] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const affiliateDetailsRef = useRef(null);  // Definir affiliateDetailsRef

  useEffect(() => {
    const userData = getUserData();
    setCurrentUser(userData);
    fetchAfiliados();
  }, []);

  const isAdmin = currentUser?.rol === 'administrador';
  const isEncargado = currentUser?.rol === 'encargado_de_sector';
  const canViewAllAffiliates = hasPermission(currentUser?.rol, PERMISSIONS.VIEW_ALL_AFFILIATES);
  const canViewSectorAffiliates = hasPermission(currentUser?.rol, PERMISSIONS.VIEW_SECTOR_AFFILIATES);
  const canCreateAffiliate = hasPermission(currentUser?.rol, PERMISSIONS.CREATE_AFFILIATE);
  const canEditAffiliate = hasPermission(currentUser?.rol, PERMISSIONS.EDIT_AFFILIATE);
  const canDeleteAffiliate = hasPermission(currentUser?.rol, PERMISSIONS.DELETE_AFFILIATE);

  const fetchAfiliados = async () => {
    setIsLoading(true);
    try {
      const afiliadosData = await api.getAfiliados();
      console.log('Afiliados raw data:', afiliadosData);
      
      // Si es encargado, filtrar solo los de su sector
      let filteredAfiliados = afiliadosData;
      if (isEncargado && currentUser?.sector_id) {
        console.log('Filtrando por sector:', currentUser.sector_id);
        filteredAfiliados = afiliadosData.filter(a => a.sector == currentUser.sector_id);
      }
      
      console.log('Filtered afiliados:', filteredAfiliados);
      
      // Filtrar afiliados únicos por cédula
      const uniqueAfiliados = filteredAfiliados.filter(
        (afiliado, index, self) =>
          index === self.findIndex((a) => a.cedulaTitular === afiliado.cedulaTitular)
      );

      // Obtener lista única de sectores
      const sectoresUnicos = [...new Set(uniqueAfiliados.map(afiliado => afiliado.sector))].filter(Boolean).sort();
      
      console.log('Unique afiliados:', uniqueAfiliados);
      console.log('Sectores únicos:', sectoresUnicos);
      
      setAfiliados(uniqueAfiliados);
      setSectores(sectoresUnicos);
    } catch (error) {
      console.error('Error al obtener los afiliados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccion = async (afiliadoId) => {
    try {
      const afiliado = await api.getAffiliado(afiliadoId);
      setSelectedAfiliado(afiliado);
      setIsEditing(false);
    } catch (error) {
      console.error('Error al obtener detalles del afiliado:', error);
      alert('Error al cargar los detalles del afiliado');
    }
  };

  const handleModalClose = () => {
    setShowAddForm(false);
    setIsEditing(false);
    setSelectedAfiliado(null);
  };

  const handleSaveAfiliado = async (newAfiliado) => {
    try {
      if (isEditing && selectedAfiliado?.idAfiliado) {
        // Actualizar afiliado existente
        await api.updateAfiliado(selectedAfiliado.idAfiliado, newAfiliado);
      } else {
        // Verificar si es un nuevo afiliado
        const exists = afiliados.some((afiliado) => afiliado.cedulaTitular === newAfiliado.cedulaTitular);

        if (exists) {
          alert('El afiliado ya está registrado.');
          return;
        }

        await api.createAfiliado(newAfiliado);
      }
      
      // Recargar los datos después de guardar
      handleModalClose();
      await fetchAfiliados();
    } catch (error) {
      console.error('Error al guardar el afiliado:', error);
      alert('Error al guardar el afiliado. Por favor, intente nuevamente.');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setShowAddForm(true);
  };

  const handleDelete = async () => {
    if (!selectedAfiliado) {
      alert('No hay afiliado seleccionado para eliminar.');
      return;
    }

    if (window.confirm(`¿Está seguro que desea eliminar al afiliado: ${selectedAfiliado.nombresTitular}?`)) {
      try {
        await api.deleteAfiliado(selectedAfiliado.idAfiliado);
        alert('Afiliado eliminado correctamente.');

        // Actualiza la lista de afiliados
        await fetchAfiliados();
        handleModalClose();
      } catch (error) {
        console.error('Error al eliminar el afiliado:', error);
        alert('No se pudo eliminar el afiliado. Verifique la conexión y los permisos.');
      }
    }
  };

  // Nueva función para descargar PDF
  const handleGeneratePDF = () => {
    const doc = new jsPDF();
  
    // Obtener el logotipo desde el DOM
    const logoImg = document.querySelector("#affiliate-details img");
  
    if (logoImg) {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = logoImg.width;
      canvas.height = logoImg.height;
      context.drawImage(logoImg, 0, 0, canvas.width, canvas.height);
  
      const logoBase64 = canvas.toDataURL("image/png");
      doc.addImage(logoBase64, "PNG", 80, 10, 50, 50); // Centrar logotipo arriba
    }
  
    let yPosition = 70;
    let fontSize = 10;
    const maxHeight = 280;
    const pageWidth = doc.internal.pageSize.width;
  
    // Configurar encabezado
    doc.setFontSize(fontSize);
    doc.setFont("Helvetica", "bold");
    const headerText = "Escuela Laboral Dr JOSE FRANCISCO PENA GOMEZ";
    const headerWidth = doc.getTextWidth(headerText);
    const headerX = (pageWidth - headerWidth) / 2;  // Centrar el texto
    doc.text(headerText, headerX, yPosition);
    yPosition += 10;
  
    doc.setFontSize(8);
    doc.setFont("Helvetica", "normal");
    const subHeaderText = "Incorporada Decreto 622-01 de 25/06/2001";
    const subHeaderWidth = doc.getTextWidth(subHeaderText);
    const subHeaderX = (pageWidth - subHeaderWidth) / 2;
    doc.text(subHeaderText, subHeaderX, yPosition);
    yPosition += 10;
  
    doc.text("RNC - 417-00701-9", (pageWidth - doc.getTextWidth("RNC - 417-00701-9")) / 2, yPosition);
    yPosition += 10;
    doc.text("C/Nueva Generación #2, Galván Prov. Bahoruco, Rep. Dom", (pageWidth - doc.getTextWidth("C/Nueva Generación #2, Galván Prov. Bahoruco, Rep. Dom")) / 2, yPosition);
    yPosition += 10;
    doc.text("TEL: 829-88-9906 / 809-248-1923", (pageWidth - doc.getTextWidth("TEL: 829-88-9906 / 809-248-1923")) / 2, yPosition);
  
    doc.line(20, yPosition + 10, 190, yPosition + 10);
    yPosition += 20;
  
    // Función para agregar texto
    const addText = (text, size = 8) => {
      const maxWidth = 180;
      const lineHeight = 7;
  
      doc.setFontSize(size);
      doc.setFont("Helvetica", "normal");
  
      let lines = doc.splitTextToSize(text, maxWidth);
  
      lines.forEach((line) => {
        if (yPosition + lineHeight > maxHeight) {
          doc.addPage();
          yPosition = 20; // Reiniciar la posición Y para la nueva página
        }
        doc.text(line, 20, yPosition);
        yPosition += lineHeight;
      });
    };
  
    // Detalles del afiliado
    doc.setFontSize(12);
    doc.setFont("Helvetica", "bold");
    const detailTitle = "Detalles del Afiliado";
    doc.text(detailTitle, (pageWidth - doc.getTextWidth(detailTitle)) / 2, yPosition);
    yPosition += 10;
  
    addText(`Código Afiliado: ${selectedAfiliado.codigoAfiliado}`);
    addText(`Sector: ${selectedAfiliado.sector}`);
    addText(`Nombre: ${selectedAfiliado.nombresTitular} ${selectedAfiliado.apellidosTitular}`);
    addText(`Cédula: ${selectedAfiliado.cedulaTitular}`);
    addText(`Teléfono: ${selectedAfiliado.telefonoTitular}`);
    addText(`Dirección: ${selectedAfiliado.direccionTitular}`);
    addText(`Total a Pagar Mensual: ${selectedAfiliado.totalPagarMensual}`);
    addText(`Fecha de Inicio de Afiliación: ${new Date(selectedAfiliado.fechaInicioAfiliacion).toLocaleDateString()}`);
    addText(`Estado: ${selectedAfiliado.estado}`);
    addText(`Último Pago: ${new Date(selectedAfiliado.ultimoPago).toLocaleDateString()}`);
  
    if (selectedAfiliado.dependencias?.length > 0) {
      doc.setFontSize(12);
      doc.setFont("Helvetica", "bold");
      doc.text("Dependencias:", (pageWidth - doc.getTextWidth("Dependencias:")) / 2, yPosition);
      yPosition += 10;
  
      selectedAfiliado.dependencias.forEach((dependencia) => {
        addText(`- ${dependencia.nombre} ${dependencia.apellido} (${dependencia.parentesco}, ${dependencia.cedula})`);
      });
    }
  
    // Firmas (alineadas y centradas)
    const signatureLineY = yPosition + 3;
  
    // Presidente Ayuda Mutua (línea más corta y centrada)
    const lineStartX = (pageWidth - 60) / 2;  // Centrar la línea
    const lineEndX = lineStartX + 60;        // Línea de 60 unidades
    doc.line(lineStartX, signatureLineY, lineEndX, signatureLineY); // Línea para el presidente
    doc.setFontSize(10);
    doc.setFont("Helvetica", "normal");
    const presidenteText = "Presidente Ayuda Mutua:";
    doc.text(presidenteText, (lineStartX + lineEndX - doc.getTextWidth(presidenteText)) / 2, signatureLineY + 7); // Texto centrado debajo de la línea
  
    yPosition = signatureLineY + 15; // Espaciado después de la firma
  
    // Representante del Sector (línea más corta y centrada)
    doc.line(lineStartX, yPosition + 3, lineEndX, yPosition + 3); // Línea para el representante
    doc.setFontSize(10);
    doc.setFont("Helvetica", "normal");
    const representanteText = "Representante del Sector:";
    doc.text(representanteText, (lineStartX + lineEndX - doc.getTextWidth(representanteText)) / 2, yPosition + 7); // Texto centrado debajo de la línea
  
    yPosition += 15; // Espaciado después de la firma
  
    // Titular Afiliado (línea más corta y centrada)
    doc.line(lineStartX, yPosition + 3, lineEndX, yPosition + 3); // Línea para el titular
    doc.setFontSize(10);
    doc.setFont("Helvetica", "normal");
    const titularText = "Titular Afiliado:";
    doc.text(titularText, (lineStartX + lineEndX - doc.getTextWidth(titularText)) / 2, yPosition + 7); // Texto centrado debajo de la línea
  
    // Guardar PDF
    doc.save("afiliado_detalles.pdf");
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const affiliateDetails = affiliateDetailsRef.current;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Detalles del Afiliado</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              padding: 40px; 
            }
            .logo { 
              width: 150px; 
              display: block; 
              margin: 0 auto 20px; 
            }
            .info { 
              text-align: center; 
              margin-bottom: 20px; 
            }
            .content { 
              max-width: 800px; 
              margin: 0 auto; 
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            .signatures-section {
              margin-top: 80px;
              display: flex;
              justify-content: space-between;
              padding: 0 40px;
            }
            .signature-block {
              text-align: center;
              width: 200px;
            }
            .signature-line {
              border-top: 1px solid black;
              margin: 50px auto 10px;
              width: 160px;
            }
            .signature-title {
              font-size: 12px;
              margin-top: 5px;
            }
            /* Ocultar los botones al imprimir */
            .buttonsContainer, button {
              display: none !important;
            }
          </style>
        </head>
        <body>
          ${affiliateDetails.innerHTML}
          <div class="signatures-section">
            <div class="signature-block">
              <div class="signature-line"></div>
              <div class="signature-title">Presidente Ayuda Mutua</div>
              <div class="signature-title">Fecha: ____/____/____</div>
            </div>
            <div class="signature-block">
              <div class="signature-line"></div>
              <div class="signature-title">Representante del Sector</div>
              <div class="signature-title">Fecha: ____/____/____</div>
            </div>
            <div class="signature-block">
              <div class="signature-line"></div>
              <div class="signature-title">Titular Afiliado</div>
              <div class="signature-title">Fecha: ____/____/____</div>
            </div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className={styles.affiliatesContainer}>
      {!showAddForm && !selectedAfiliado && (
        <></>
      )}

      {showAddForm ? (
        <AddAffiliateForm 
          onSave={handleSaveAfiliado} 
          onClose={handleModalClose}
          initialData={isEditing ? selectedAfiliado : null}
          isEditing={isEditing}
        />
      ) : selectedAfiliado ? (
        <div className={styles.affiliateDetails} ref={affiliateDetailsRef} id="affiliate-details">
          <div className={styles.logoContainer} style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '10px 0 0 0'  // Eliminado el margen inferior
          }}>
            <img 
              src={logo} 
              alt="Logo de la Empresa" 
              className={styles.logo} 
              style={{
                width: '150px',
                height: 'auto',
                display: 'block',
                marginBottom: '-10px'  // Margen negativo para acercar al título
              }}
            />
          </div>
          <div className={styles.info}>
            <p style={{ 
              textAlign: 'center', 
              width: '100%', 
              margin: '-10px auto 0',  // Aumentado el margen negativo superior
              lineHeight: '1.2',       // Reducido el interlineado
              fontSize: '14px'
            }}>
              <span style={{ 
                display: 'block', 
                fontWeight: 'bold', 
                fontSize: '16px', 
                marginBottom: '2px'    // Reducido aún más el espacio inferior
              }}>
                Escuela Laboral Dr JOSE FRANCISCO PENA GOMEZ
              </span>
              <span style={{ display: 'block', marginBottom: '0px' }}>
                Incorporada Decreto 622-01 de 25/06/2001
              </span>
              <span style={{ display: 'block', marginBottom: '0px' }}>
                RNC - 417-00701-9
              </span>
              <span style={{ display: 'block', marginBottom: '0px' }}>
                C/Nueva Generación #2
              </span>
              <span style={{ display: 'block', marginBottom: '0px' }}>
                Galván Prov. Bahoruco
              </span>
              <span style={{ display: 'block', marginBottom: '0px' }}>
                Rep. Dom
              </span>
              <span style={{ display: 'block', marginBottom: '0px' }}>
                TEL: 829-881-9906 / 809-248-1923
              </span>
              <div style={{ 
                width: '100%',
                borderBottom: '1px solid black',
                margin: '8px auto'
              }}></div>
              <span style={{ 
                display: 'block', 
                fontWeight: 'bold',
                fontSize: '15px',
                letterSpacing: '1px',
                textAlign: 'center',
                width: '100%',
                margin: '5px auto'
              }}>
                AYUDA MUTUA
              </span>
            </p>
          </div>
          <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Detalles del Afiliado</h3>
          <div style={{ fontSize: '13px', lineHeight: '1.3' }}>
            <p style={{ margin: '4px 0' }}><strong>Código Afiliado:</strong> {selectedAfiliado.codigoAfiliado}</p>
            <p style={{ margin: '4px 0' }}><strong>Sector:</strong> {selectedAfiliado.sector}</p>
            <p style={{ margin: '4px 0' }}><strong>Nombre:</strong> {selectedAfiliado.nombresTitular} {selectedAfiliado.apellidosTitular}</p>
            <p style={{ margin: '4px 0' }}><strong>Cédula:</strong> {selectedAfiliado.cedulaTitular}</p>
            <p style={{ margin: '4px 0' }}><strong>Teléfono:</strong> {selectedAfiliado.telefonoTitular}</p>
            <p style={{ margin: '4px 0' }}><strong>Dirección:</strong> {selectedAfiliado.direccionTitular}</p>
            <p style={{ margin: '4px 0' }}><strong>Total a Pagar Mensual:</strong> {selectedAfiliado.totalPagarMensual}</p>
            <p style={{ margin: '4px 0' }}><strong>Fecha de Inicio de Afiliación:</strong> {new Date(selectedAfiliado.fechaInicioAfiliacion).toLocaleDateString()}</p>
            <p style={{ margin: '4px 0' }}><strong>Estado:</strong> {selectedAfiliado.estado || 'Activo'}</p>
            <p style={{ margin: '4px 0' }}><strong>Último Pago:</strong> {selectedAfiliado.ultimoPago ? new Date(selectedAfiliado.ultimoPago).toLocaleDateString() : 'No hay pagos registrados'}</p>
          </div>

          <div className={styles.dependenciasContainer} style={{ fontSize: '13px', marginTop: '10px' }}>
            {selectedAfiliado.dependencias?.length > 0 ? (
              <table className={styles.dependenciasTable}>
                <thead>
                  <tr>
                    <th style={{ padding: '4px 8px' }}>Nombre</th>
                    <th style={{ padding: '4px 8px' }}>Parentesco</th>
                    <th style={{ padding: '4px 8px' }}>Cédula</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedAfiliado.dependencias.map((dependencia, index) => (
                    <tr key={index}>
                      <td style={{ padding: '4px 8px' }}>{dependencia.nombre} {dependencia.apellido}</td>
                      <td style={{ padding: '4px 8px' }}>{dependencia.parentesco}</td>
                      <td style={{ padding: '4px 8px' }}>{dependencia.cedula}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No hay dependencias registradas.</p>
            )}
          </div>

          <div className={styles.signatureSection} style={{
            marginTop: '15px',
            marginBottom: '15px'
          }}>
            <div className={styles.signatureContainer} style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              gap: '10px'
            }}>
              <div className={styles.signatureField} style={{ flex: 1, textAlign: 'center' }}>
                <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Presidente Ayuda Mutua</p>
                <div style={{ 
                  borderTop: '1px solid black',
                  width: '80%',
                  margin: '0 auto'
                }}></div>
              </div>
              <div className={styles.signatureField} style={{ flex: 1, textAlign: 'center' }}>
                <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Representante del Sector</p>
                <div style={{ 
                  borderTop: '1px solid black',
                  width: '80%',
                  margin: '0 auto'
                }}></div>
              </div>
              <div className={styles.signatureField} style={{ flex: 1, textAlign: 'center' }}>
                <p style={{ fontSize: '12px', margin: '0 0 5px 0' }}>Titular Afiliado</p>
                <div style={{ 
                  borderTop: '1px solid black',
                  width: '80%',
                  margin: '0 auto'
                }}></div>
              </div>
            </div>
          </div>

          <div className={styles.buttonsContainer}>
            <button onClick={() => setSelectedAfiliado(null)} className={styles.button}>Cerrar Detalles</button>
            {canEditAffiliate && (
              <button onClick={handleEdit} className={styles.button}>Editar</button>
            )}
            {canDeleteAffiliate && (
              <button onClick={handleDelete} className={styles.button}>Eliminar</button>
            )}
            <button 
              onClick={handleGeneratePDF} 
              className={`${styles.actionButton} ${styles.pdfButton}`}
            >
              <i className="fas fa-file-pdf"></i>
              <span>PDF</span>
            </button>
            <button 
              onClick={handlePrint}
              className={`${styles.actionButton} ${styles.printButton}`}
            >
              <i className="fas fa-print"></i>
              <span>Imprimir</span>
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.affiliatesTableWrapper}>
          <div className={styles.searchContainer} style={{
            marginBottom: '25px',
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}>
            {/* Desktop layout */}
            <div className={styles.filterControls} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              justifyContent: 'space-between',
              flexWrap: 'wrap'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: 1,
                minWidth: '200px'
              }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  minWidth: '60px',
                  whiteSpace: 'nowrap'
                }}>Buscar:</label>
                <input
                  type="text"
                  placeholder="Nombre, código o cédula..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '14px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid #4CAF50';
                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #ddd';
                    e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                  }}
                />
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flex: '0 1 auto',
                minWidth: '200px'
              }}>
                <label htmlFor="sector-select" style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto'
                }}>Sector:</label>
                <select
                  id="sector-select"
                  value={sectorSeleccionado}
                  onChange={(e) => setSectorSeleccionado(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontSize: '13px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    fontFamily: 'inherit',
                    minWidth: 'auto'
                  }}
                  onFocus={(e) => {
                    e.target.style.border = '1px solid #4CAF50';
                    e.target.style.boxShadow = '0 0 0 3px rgba(76, 175, 80, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.border = '1px solid #ddd';
                    e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                  }}
                >
                  <option value="">Todos</option>
                  {sectores.map((sector) => (
                    <option key={sector} value={sector}>
                      {sector}
                    </option>
                  ))}
                </select>
              </div>
              {canCreateAffiliate && (
                <button 
                  onClick={() => setShowAddForm(true)}
                  style={{
                    background: '#003366',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(0, 51, 102, 0.2)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 51, 102, 0.3)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.backgroundColor = '#001a33';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 51, 102, 0.2)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.backgroundColor = '#003366';
                  }}
                >
                  + Agregar
                </button>
              )}
            </div>
          </div>

          <table className={styles.affiliatesTable}>
            <thead>
              <tr>
                <th>Fecha de Afiliacion</th>
                <th>Código Afiliado</th>
                <th>Nombre y Apellido</th>
                <th>Sector</th>
                <th>Cuota Mensual</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {afiliados.length > 0 ? (
                afiliados
                  .filter(afiliado => {
                    const matchesSector = !sectorSeleccionado || afiliado.sector === sectorSeleccionado;
                    const matchesSearch = !searchTerm || 
                      afiliado.nombresTitular?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      afiliado.apellidosTitular?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      afiliado.codigoAfiliado?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      afiliado.cedulaTitular?.toLowerCase().includes(searchTerm.toLowerCase());
                    return matchesSector && matchesSearch;
                  })
                  .map((afiliado) => (
                    <tr key={afiliado.id}>
                      <td>{new Date(afiliado.fechaInicioAfiliacion).toLocaleDateString()}</td>
                      <td>{afiliado.codigoAfiliado}</td>
                      <td>{afiliado.nombresTitular} {afiliado.apellidosTitular}</td>
                      <td>{afiliado.sector}</td>
                      <td>RD$ {parseFloat(afiliado.totalPagarMensual || 0).toFixed(2)}</td>
                      <td>
                        <button 
                          onClick={() => handleAccion(afiliado.idAfiliado)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '5px',
                            transition: 'transform 0.2s ease, color 0.2s ease',
                            color: '#007bff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.3)';
                            e.currentTarget.style.color = '#0056b3';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.color = '#007bff';
                          }}
                          title="Ver detalles"
                        >
                          <FiEye size={22} />
                        </button>
                      </td>
                    </tr>
                  ))
              ) : isLoading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #e9ecef',
                        borderTop: '4px solid #007bff',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span style={{ fontSize: '15px', color: '#666', fontWeight: '500' }}>Cargando afiliados...</span>
                    </div>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No hay afiliados registrados.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Affiliates;